import { useEffect, useState, useRef, useMemo, useCallback, useImperativeHandle, MutableRef, Ref } from 'preact/hooks';
import { forwardRef } from 'preact/compat';

// D3 and topojson.
import * as d3 from 'd3';
import * as topojson from 'topojson-client';

// Data.
import usa from './data/states-albers-10m.json';

// Utils.
import mapSettings from './utils/mapSettings';
import { zoomIn, zoomOut, zoomReset } from './utils/zoom';

// Hooks.
import useFloatingTooltip from './hooks/useFloatingTooltip';

// Components.
import { Regions } from './Regions';
import { LocationPoints } from './LocationPoints';
import FloatingTooltip from './FloatingTooltip';

export type MapHandle = {
	zoomIn: () => void;
	zoomOut: () => void;
	zoomReset: () => void;
};

// Global map settings.
const { mapScaleMin, mapScaleMax } = mapSettings;

/**
 * Zoom transform hook.
 * Use zoom behavior to set map transform.
 *
 * @param  svgRef - d3 svg reference
 * @return {Array} Zoom behavior, transform, and zoom state
 */
const useZoomTransform = ( svgRef: MutableRef<Element | null> ): [ d3.ZoomBehavior<Element, unknown>, d3.ZoomTransform, boolean ] => {
	// This is the transform we apply to the zoomable area of the map.
	const [ mapTransform, setMapTransform ] = useState( d3.zoomIdentity );

	// Indicate if we are zooming.
	const [ isZooming, setIsZooming ] = useState( false );

	// Use a ref to store the zoom function to prevent unnecessary re-creation.
	const zoomRef = useRef<d3.ZoomBehavior<Element, unknown>>();

	// Create zoom callback only once.
	const zoomed = useCallback( ( event: { transform: d3.ZoomTransform } ) => {
		const { transform } = event;
		setIsZooming( true );
		setMapTransform( transform );
	}, [] );

	// Create zoom end callback only once.
	const zoomEnd = useCallback( () => {
		setIsZooming( false );
	}, [] );

	// Initialize zoom once.
	if ( ! zoomRef.current ) {
		zoomRef.current = d3
			.zoom()
			.scaleExtent( [ mapScaleMin, mapScaleMax ] )
			.filter( ( event ) => {
				// Disable right-click drag (context menu should work normally).
				if ( event.button === 2 ) {
					return false;
				}

				// Allow wheel + Ctrl/Command for users who specifically want to zoom with the wheel.
				if ( event.type === 'wheel' && ( ! event.ctrlKey && ! event.metaKey ) ) {
					return false;
				}

				// Return true by default to allow zoom.
				return true;
			} )
			.on( 'zoom', zoomed )
			.on( 'end', zoomEnd );
	}

	useEffect( () => {
		if ( ! svgRef.current || ! zoomRef.current ) {
			return;
		}

		const svg = d3.select( svgRef.current );
		svg.call( zoomRef.current );

		// Cleanup when unmounting.
		return () => {
			svg.on( '.zoom', null );
		};
	}, [ svgRef ] );

	return [ zoomRef.current, mapTransform, isZooming ];
};

/**
 * Hook to calculate US map data from topojson.
 *
 * @return {Object} Object containing path generator, map height, and processed regions with path data.
 */
const useUSMapData = () => {
	return useMemo( () => {
		// AlbersUSA projection is baked into the topojson data.
		const pathGenerator = d3.geoPath();
		const regionsData = topojson.feature( usa, usa.objects.states );

		// Pre-calculate regions with path data to avoid recalculation.
		const regionsWithPathData: SingleRegion[] = [];
		regionsData.features.forEach( ( region ) => {
			const d = pathGenerator( region );
			if ( ! d ) {
				return;
			}
			regionsWithPathData.push( {
				...region,
				d,
				centroid: pathGenerator.centroid( region ),
			} );
		} );

		return {
			pathGenerator,
			regionsWithPathData,
		};
	}, [] );
};

type MapProps = {
	locations: MapLocation[];
	onLocationClick: ( index: number ) => void;
	selectedIndex: number | null;
	filteredLocations: { location: MapLocation; originalIndex: number }[];
};

/**
 * Map Component. Renders a US map with state regions.
 * Exposes zoom methods via ref for external zoom controls.
 *
 * @param {Object}          props                 - component props
 * @param {MapLocation[]}   props.locations        - array of location data from the REST API.
 * @param {Function}        props.onLocationClick  - callback when a location dot is clicked.
 * @param {Ref}             ref                    - forwarded ref exposing zoom methods.
 */
const Map = forwardRef( function Map( { locations, onLocationClick, selectedIndex, filteredLocations }: MapProps, ref: Ref< MapHandle > ) {
	// Create a ref for the svg element.
	const svgRef = useRef< SVGSVGElement >( null );

	// Create a ref for the map wrapper (used for tooltip hover detection).
	const wrapperRef = useRef< HTMLDivElement >( null );

	// Get US map data.
	const { regionsWithPathData } = useUSMapData();

	// Create a projection for converting lat/lng to pixel coordinates.
	// Uses standard US Atlas parameters matching the pre-baked topojson viewBox (975x610).
	const locationProjection = useMemo(
		() => d3.geoAlbersUsa().scale( 1300 ).translate( [ 487.5, 305 ] ),
		[]
	);

	// Get zoom function and transform object.
	const [ zoom, mapTransform, isZooming ] = useZoomTransform( svgRef );

	// Floating tooltip for location points.
	const {
		activeIndex,
		showTooltip,
		floatingStyles,
		floatingRefs,
		getReferenceProps,
		getFloatingProps,
		isPositioned,
		placement,
	} = useFloatingTooltip( {
		wrapperRef,
		locations,
	} );

	// Convert original selected index to filtered index for LocationPoints.
	const selectedFilteredIndex = useMemo( () => {
		if ( selectedIndex === null ) {
			return null;
		}
		const idx = filteredLocations.findIndex( ( { originalIndex } ) => originalIndex === selectedIndex );
		return idx >= 0 ? idx : null;
	}, [ selectedIndex, filteredLocations ] );

	// Expose zoom methods to parent via ref.
	useImperativeHandle( ref, () => ( {
		zoomIn: () => zoomIn( svgRef, zoom ),
		zoomOut: () => zoomOut( svgRef, zoom ),
		zoomReset: () => zoomReset( svgRef, zoom ),
	} ), [ zoom ] );

	return (
		<div
			className={ `str-maps__map ${ isZooming ? 'str-maps__map--is-zooming' : '' }` }
			ref={ wrapperRef }
			{ ...getReferenceProps() }
		>
			<svg
				className="str-maps__svg"
				ref={ svgRef }
				viewBox="0 0 975 610"
				role="application"
				aria-label="Interactive map of the United States. Use zoom controls to navigate."
			>
				<g
					className="str-maps__zoom-group"
					transform={ mapTransform?.toString() ?? '' }
				>
					<Regions regions={ regionsWithPathData } />
					{ locations.length > 0 && (
						<LocationPoints
							locations={ locations }
							projection={ locationProjection }
							scaleFactor={ mapTransform.k }
							hoveredIndex={ activeIndex }
							selectedIndex={ selectedFilteredIndex }
							onLocationClick={ onLocationClick }
						/>
					) }
				</g>
			</svg>

			{ showTooltip && activeIndex !== null && (
				<FloatingTooltip
					location={ locations[ activeIndex ] }
					floatingStyles={ floatingStyles }
					refCallback={ floatingRefs.setFloating }
					getFloatingProps={ getFloatingProps }
					isPositioned={ isPositioned }
					placement={ placement }
				/>
			) }
		</div>
	);
} );

export default Map;
